// Normalize each mean waveform over time using sample standard deviation,
// matching the z-score convention used by CellExplorer/MATLAB.
export const zScoreWaveform = (values) => {
  if (!values.length || !values.every(Number.isFinite)) return [];
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / Math.max(1, values.length - 1);
  const deviation = Math.sqrt(variance);
  return values.map(value => deviation > 0 ? (value - mean) / deviation : 0);
};

export const meanWaveformSeries = (id, waveforms = [], label = `Cluster ${id}`) => {
  const valid = waveforms.filter(w => Array.isArray(w.timePoints) && w.timePoints.length > 1
    && w.amplitude?.length === w.timePoints.length && w.amplitude.every(Number.isFinite)
    && w.timePoints.every(Number.isFinite));
  if (!valid.length) return null;
  const x = valid[0].timePoints;
  const aligned = valid.filter(w => w.timePoints.length === x.length && w.timePoints.every((v, i) => v === x[i]));
  return { id, label, x, y: x.map((_, i) => aligned.reduce((sum, w) => sum + w.amplitude[i], 0) / aligned.length) };
};

// Interpolate onto the first waveform's time grid; never stretch or extrapolate rows.
const interpolate = (x, y, target) => target.map(time => {
  if (time < x[0] || time > x[x.length - 1]) return null;
  const right = x.findIndex(value => value >= time);
  if (right === 0 || x[right] === time) return y[right];
  const fraction = (time - x[right - 1]) / (x[right] - x[right - 1]);
  return y[right - 1] + fraction * (y[right] - y[right - 1]);
});

export const normalizedWaveformTraces = (series, mode, selectedIds = []) => {
  const selected = new Set(selectedIds.map(String));
  const rows = series.filter(Boolean).filter(row => row.x?.length > 1 && row.x.length === row.y?.length
    && row.x.every((value, i) => Number.isFinite(value) && (!i || value > row.x[i - 1])))
    .map(row => ({ ...row, z: zScoreWaveform(row.y) })).filter(row => row.z.length);
  if (mode === 'heatmap') {
    if (!rows.length) return [];
    return [{
      type: 'heatmap', x: rows[0].x, y: rows.map(row => row.label),
      z: rows.map(row => interpolate(row.x, row.z, rows[0].x)),
      customdata: rows.map(row => rows[0].x.map(() => row.id)),
      colorscale: 'RdBu', reversescale: true, zmid: 0,
      colorbar: { title: 'Z-score' }, hoverongaps: false,
      hovertemplate: '%{y}<br>Time: %{x:.3f} ms<br>Z-score: %{z:.2f}<extra></extra>',
    }];
  }
  return rows.map((row, index) => ({
    type: 'scatter', mode: 'lines', x: row.x, y: row.z, name: row.label,
    customdata: row.x.map(() => row.id),
    line: { color: `hsl(${index * 137.508 % 360},70%,60%)`, width: selected.has(String(row.id)) ? 4 : 2 },
    hovertemplate: `${row.label}<br>Time: %{x:.3f} ms<br>Z-score: %{y:.2f}<extra></extra>`,
  }));
};


// Keep each sampled spike separate when inspecting a cluster; retain its source ID.
export const spikeWaveformSeries = (waveforms = []) => waveforms
  .map((waveform, index) => ({
    id: waveform.spikeIndex ?? index,
    label: `Spike ${waveform.spikeIndex ?? index}`,
    x: waveform.timePoints,
    y: waveform.amplitude,
  }))
  .filter(row => Array.isArray(row.x) && row.x.length > 1 && Array.isArray(row.y)
    && row.y.length === row.x.length && row.y.every(Number.isFinite)
    && row.x.every((value, index) => Number.isFinite(value) && (!index || value > row.x[index - 1])));
